import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  PencilIcon, 
  TrashIcon, 
  PlusIcon,
  Bars3Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  TagIcon,
  BuildingStorefrontIcon,
  HomeIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  HeartIcon,
  BookOpenIcon,
  MusicalNoteIcon,
  CameraIcon,
  SparklesIcon,
  GiftIcon,
  BeakerIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  MapIcon,
  StarIcon,
  BuildingOfficeIcon,
  ShoppingBagIcon,
  UserGroupIcon,
  TvIcon,
  ClockIcon,
  GlobeAltIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { categoriesApi, CategoryAttribute, Category } from '../services/api';
import { adminApi, AdminCategoryDto } from '../services/adminApi';
import { useConfirmationModal, modalPresets } from '../hooks/useConfirmationModal';
import ConfirmationModal from '../components/ConfirmationModal';

// Category Icons for selection
const CATEGORY_ICONS = [
  { name: 'Home & Garden', icon: HomeIcon, value: 'home' },
  { name: 'Automotive', icon: TruckIcon, value: 'car' },
  { name: 'Electronics', icon: ComputerDesktopIcon, value: 'electronics' },
  { name: 'Mobile', icon: DevicePhoneMobileIcon, value: 'mobile' },
  { name: 'Shop', icon: BuildingStorefrontIcon, value: 'shop' },
  { name: 'Tools', icon: WrenchScrewdriverIcon, value: 'tools' },
  { name: 'Beauty', icon: HeartIcon, value: 'beauty' },
  { name: 'Books', icon: BookOpenIcon, value: 'book' },
  { name: 'Music', icon: MusicalNoteIcon, value: 'music' },
  { name: 'Photography', icon: CameraIcon, value: 'camera' },
  { name: 'Clothing', icon: SparklesIcon, value: 'clothing' },
  { name: 'Gifts', icon: GiftIcon, value: 'gift' },
  { name: 'Science', icon: BeakerIcon, value: 'science' },
  { name: 'Education', icon: AcademicCapIcon, value: 'education' },
  { name: 'Business', icon: BriefcaseIcon, value: 'business' },
  { name: 'Travel', icon: MapIcon, value: 'travel' },
  { name: 'Sports', icon: StarIcon, value: 'sports' },
  { name: 'General', icon: TagIcon, value: 'general' }
];

const AdminCategoriesPage: React.FC = () => {
  const [selectedCategoryForAttributes, setSelectedCategoryForAttributes] = useState<string>('');
  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AdminCategoryDto | null>(null);
  const [editingAttribute, setEditingAttribute] = useState<CategoryAttribute | null>(null);
  const [draggedCategory, setDraggedCategory] = useState<string | null>(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [localCategories, setLocalCategories] = useState<AdminCategoryDto[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  
  // Confirmation modal hook
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    iconUrl: 'general',
    parentCategoryId: '',
    sortOrder: 0
  });

  const [attributeForm, setAttributeForm] = useState({
    name: '',
    label: '',
    type: 'Text',
    isRequired: false,
    isFilterable: false,
    isSearchable: false,
    sortOrder: 0,
    options: '',
    defaultValue: ''
  });

  // Fetch categories
  const { data: categories, refetch: refetchCategories } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => adminApi.getCategories()
  });

  // Update local categories when remote data changes
  React.useEffect(() => {
    if (categories?.data) {
      // The API returns hierarchical structure, so we need to flatten it for our state management
      const flattenedCategories: AdminCategoryDto[] = [];
      
      categories.data.forEach((parent: AdminCategoryDto) => {
        // Add parent category
        flattenedCategories.push({
          ...parent,
          subCategories: [] // Clear subCategories since we're flattening
        });
        
        // Add child categories (level 2)
        if (parent.subCategories && parent.subCategories.length > 0) {
          parent.subCategories.forEach((child: AdminCategoryDto) => {
            flattenedCategories.push({
              ...child,
              parentCategoryId: parent.id,
              subCategories: []
            });
            
            // Add sub-subcategories (level 3)
            if (child.subCategories && child.subCategories.length > 0) {
              child.subCategories.forEach((subChild: AdminCategoryDto) => {
                flattenedCategories.push({
                  ...subChild,
                  parentCategoryId: child.id,
                  subCategories: []
                });
              });
            }
          });
        }
      });
      
      setLocalCategories(flattenedCategories);
    }
  }, [categories?.data]);

  // Fetch category attributes
  const { data: categoryAttributes, refetch: refetchAttributes } = useQuery({
    queryKey: ['admin-category-attributes', selectedCategoryForAttributes],
    queryFn: () => adminApi.getCategoryAttributes(selectedCategoryForAttributes),
    enabled: !!selectedCategoryForAttributes
  });

  // Organize categories into hierarchical structure
  const organizeCategories = () => {
    if (!localCategories?.length) return [];

    // First, get all parent categories (no parentCategoryId)
    const parentCategories = localCategories
      .filter(cat => !cat.parentCategoryId)
      .sort((a, b) => a.sortOrder - b.sortOrder);

    // Create hierarchical structure with 3 levels
    const hierarchicalCategories: Array<AdminCategoryDto & { children?: AdminCategoryDto[], level: number }> = [];

    parentCategories.forEach(parent => {
      // Get direct children of this parent (level 2)
      const directChildren = localCategories
        .filter(cat => cat.parentCategoryId === parent.id)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      
      // For each direct child, get its children (level 3)
      const childrenWithSubChildren = directChildren.map(child => {
        const subChildren = localCategories
          .filter(cat => cat.parentCategoryId === child.id)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        
        return {
          ...child,
          children: subChildren,
          level: 1
        };
      });
      
      hierarchicalCategories.push({
        ...parent,
        children: childrenWithSubChildren,
        level: 0
      });
    });

    return hierarchicalCategories;
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };



  // Get icon component by value
  const getIconComponent = (iconValue: string) => {
    const iconData = CATEGORY_ICONS.find(icon => icon.value === iconValue);
    return iconData ? iconData.icon : TagIcon;
  };

  // Get next suggested sort order
  const getNextSortOrder = () => {
    if (!localCategories?.length) return 1;
    const maxOrder = Math.max(...localCategories.map(cat => cat.sortOrder));
    return maxOrder + 1;
  };

  // Handle drag and drop
  const handleDragStart = (e: React.DragEvent, categoryId: string) => {
    setDraggedCategory(categoryId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', categoryId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetCategoryId: string) => {
    e.preventDefault();
    if (!draggedCategory || draggedCategory === targetCategoryId) {
      setDraggedCategory(null);
      return;
    }

    // Find the dragged and target categories
    const draggedCat = localCategories.find(cat => cat.id === draggedCategory);
    const targetCat = localCategories.find(cat => cat.id === targetCategoryId);
    
    if (!draggedCat || !targetCat) {
      toast.error('خطا در تغییر ترتیب');
      setDraggedCategory(null);
      return;
    }

    // Check if both categories are at the same level (both parents or both children of same parent)
    const draggedIsParent = !draggedCat.parentCategoryId;
    const targetIsParent = !targetCat.parentCategoryId;
    
    if (draggedIsParent !== targetIsParent) {
      // Different levels - check if dragged is child and target is parent (not allowed)
      if (!draggedIsParent && targetIsParent) {
        toast.error('نمی‌توان زیردسته را بالای دسته اصلی قرار داد');
        setDraggedCategory(null);
        return;
      }
      // If dragged is parent and target is child, we can allow this (moving parent near child's position)
    }

    // If both are children, they must be children of the same parent
    if (!draggedIsParent && !targetIsParent && draggedCat.parentCategoryId !== targetCat.parentCategoryId) {
      toast.error('زیردسته‌ها فقط می‌توانند در دسته والد خود مرتب شوند');
      setDraggedCategory(null);
      return;
    }

    // Get categories at the same level for reordering
    let categoriesToReorder;
    if (draggedIsParent && targetIsParent) {
      // Both are parent categories
      categoriesToReorder = localCategories.filter(cat => !cat.parentCategoryId);
    } else if (!draggedIsParent && !targetIsParent) {
      // Both are children of the same parent
      categoriesToReorder = localCategories.filter(cat => cat.parentCategoryId === draggedCat.parentCategoryId);
    } else {
      // Mixed levels - reorder within parent categories
      categoriesToReorder = localCategories.filter(cat => !cat.parentCategoryId);
    }

    // Create a new array with updated sort orders
    const updatedCategories = [...localCategories];
    
    // Find positions in the same-level array
    const draggedIndexInLevel = categoriesToReorder.findIndex(cat => cat.id === draggedCategory);
    const targetIndexInLevel = categoriesToReorder.findIndex(cat => cat.id === targetCategoryId);

    if (draggedIndexInLevel === -1 || targetIndexInLevel === -1) {
      toast.error('Error changing order');
      setDraggedCategory(null);
      return;
    }

    // Remove dragged item and insert at target position in the same-level array
    const [removedItem] = categoriesToReorder.splice(draggedIndexInLevel, 1);
    categoriesToReorder.splice(targetIndexInLevel, 0, removedItem);

    // Update sort orders for the reordered level
    categoriesToReorder.forEach((cat, index) => {
      const categoryInMain = updatedCategories.find(c => c.id === cat.id);
      if (categoryInMain) {
        categoryInMain.sortOrder = index + 1;
      }
    });

    // Update local state immediately for instant visual feedback
    setLocalCategories(updatedCategories);

    // Reset drag state and show success
    setDraggedCategory(null);
          toast.success('ترتیب دسته‌بندی به‌روزرسانی شد');

    // API call to persist changes (you can implement this)
    try {
      console.log('Category order updated:', {
        from: draggedCat.name,
        to: targetCat.name,
        newOrder: categoriesToReorder.map(cat => ({ id: cat.id, name: cat.name, sortOrder: cat.sortOrder }))
      });
      
      // Optionally refetch after a delay to ensure server sync
      setTimeout(() => {
        refetchCategories();
      }, 1000);
      
    } catch (error) {
      toast.error('خطا در ذخیره ترتیب جدید');
      console.error('Error reordering categories:', error);
      // Revert changes on error
      refetchCategories();
    }
  };

  const handleManageAttributes = (categoryId: string) => {
    setSelectedCategoryForAttributes(categoryId);
    setShowAttributeModal(true);
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      slug: '',
      description: '',
      iconUrl: 'general',
      parentCategoryId: '',
      sortOrder: getNextSortOrder()
    });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category: AdminCategoryDto) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      slug: category.slug || '',
      description: category.description || '',
      iconUrl: category.iconUrl || 'general',
      parentCategoryId: category.parentCategoryId || '',
      sortOrder: category.sortOrder
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    showModal(modalPresets.delete('این دسته‌بندی', async () => {
      try {
        await adminApi.deleteCategory(categoryId);
        toast.success('دسته‌بندی با موفقیت حذف شد');
        await refetchCategories();
      } catch (error) {
        toast.error('خطا در حذف دسته‌بندی');
        console.error('Error deleting category:', error);
      }
    }));
  };

  const handleSaveCategory = async () => {
    try {
      // Use the slug from the form (either manually entered or auto-generated)
      const slug = categoryForm.slug.trim() || `category-${Date.now()}`;

      // Prepare the data in the format expected by the backend
      const categoryData = {
        name: categoryForm.name,
        slug: slug,
        description: categoryForm.description || undefined,
        iconUrl: categoryForm.iconUrl,
        isActive: true,
        sortOrder: categoryForm.sortOrder,
        parentCategoryId: categoryForm.parentCategoryId || undefined
      };

      if (editingCategory) {
        // Update existing category
        await adminApi.updateCategory(editingCategory.id, categoryData);
        toast.success('دسته‌بندی با موفقیت به‌روزرسانی شد');
      } else {
        // Create new category
        await adminApi.createCategory(categoryData);
        toast.success('دسته‌بندی با موفقیت ایجاد شد');
      }
      
      setShowCategoryModal(false);
      await refetchCategories();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'خطا در ذخیره دسته‌بندی';
      toast.error(errorMessage);
      console.error('Error saving category:', error);
    }
  };

  const handleSaveAttribute = async () => {
    try {
      const attributeData = {
        name: attributeForm.name,
        label: attributeForm.label,
        type: attributeForm.type,
        defaultValue: attributeForm.defaultValue,
        isRequired: attributeForm.isRequired,
        isFilterable: attributeForm.isFilterable,
        isSearchable: attributeForm.isSearchable,
        sortOrder: attributeForm.sortOrder,
        options: attributeForm.options,
        validationRules: null
      };

      if (editingAttribute) {
        // Update existing attribute
        await adminApi.updateCategoryAttribute(selectedCategoryForAttributes, editingAttribute.id, attributeData);
        toast.success('ویژگی با موفقیت به‌روزرسانی شد');
      } else {
        // Create new attribute
        await adminApi.createCategoryAttribute(selectedCategoryForAttributes, attributeData);
        toast.success('ویژگی با موفقیت ایجاد شد');
      }

      // Reset form
      setAttributeForm({
        name: '',
        label: '',
        type: 'Text',
        isRequired: false,
        isFilterable: false,
        isSearchable: false,
        sortOrder: 0,
        options: '',
        defaultValue: ''
      });
      setEditingAttribute(null);
      
      // Force refresh the attributes list to show updated data
      await refetchAttributes();
      
    } catch (error) {
      toast.error('خطا در ذخیره ویژگی');
      console.error('Error saving attribute:', error);
    }
  };

  // Get parent categories for dropdown
  const parentCategories = localCategories?.filter(cat => !cat.parentCategoryId) || [];

  return (
    <div className="space-y-6" dir="rtl">
      {/* Information Panel */}
      <div className="bg-primary-50 border-r-4 border-primary-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <div className="w-5 h-5 rounded-full bg-primary-400 text-white flex items-center justify-center text-xs">ℹ</div>
          </div>
          <div className="mr-3">
                          <h3 className="text-sm font-medium text-primary-800">راهنمای مدیریت دسته‌بندی</h3>
              <div className="mt-2 text-sm text-primary-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>هم دسته‌های اصلی و هم زیردسته‌ها می‌توانند ویژگی داشته باشند</li>
                  <li>برای ایجاد زیردسته، یک دسته والد را در فرم افزودن دسته انتخاب کنید</li>
                  <li>برای مشاهده زیردسته‌ها، روی فلش کنار دسته اصلی کلیک کنید</li>
                  <li>ویژگی‌های الزامی در فرم ثبت آگهی اجباری هستند</li>
                  <li>ویژگی‌های قابل فیلتر در صفحه جستجو نمایش داده می‌شوند</li>
                </ul>
              </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">مدیریت دسته‌بندی</h3>
              <p className="mt-1 text-sm text-gray-500">
                مدیریت دسته‌بندی‌ها و تعریف ویژگی‌های خاص برای هر دسته
              </p>
            </div>
            <button
              onClick={handleAddCategory}
              className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 flex items-center space-x-2 space-x-reverse"
            >
              <PlusIcon className="h-5 w-5" />
              <span>افزودن دسته</span>
            </button>
          </div>

          {/* Categories Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ترتیب
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    آیکون
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    نام دسته
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    نوع
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    تعداد آگهی
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ویژگی‌ها
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    عملیات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {organizeCategories().map(category => {
                  const IconComponent = getIconComponent(category.iconUrl || 'general');
                  const hasChildren = category.children && category.children.length > 0;
                  const isExpanded = expandedCategories.has(category.id);

                  return (
                    <React.Fragment key={`parent-${category.id}`}>
                      {/* Parent Category Row */}
                      <tr 
                        className={`hover:bg-gray-50 ${draggedCategory === category.id ? 'opacity-50' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, category.id)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, category.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Bars3Icon 
                              className="h-4 w-4 text-gray-400 ml-2 cursor-move" 
                              title="کشیدن برای تغییر ترتیب"
                            />
                            <span className="text-sm font-medium text-gray-900">{category.sortOrder}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <IconComponent className="h-6 w-6 text-gray-600" />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {hasChildren && (
                              <button
                                onClick={() => toggleCategoryExpansion(category.id)}
                                className="ml-2 w-6 h-6 flex items-center justify-center hover:bg-gray-200 rounded transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                                ) : (
                                  <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
                                )}
                              </button>
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900 flex items-center">
                                {category.name}
                                {hasChildren && (
                                                                      <span className="ml-2 bg-primary-100 text-primary-800 text-xs px-2 py-0.5 rounded-full">
                                      {category.children?.length} زیردسته
                                    </span>
                                )}
                              </div>
                              {category.description && (
                                <div className="text-sm text-gray-500">
                                  {category.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            دسته اصلی
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">0</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleManageAttributes(category.id)}
                            className="text-purple-600 hover:text-purple-900 text-sm font-medium flex items-center space-x-1 space-x-reverse"
                          >
                            <TagIcon className="h-4 w-4" />
                            <span>مدیریت ویژگی‌ها</span>
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2 space-x-reverse">
                            <button
                              onClick={() => handleEditCategory(category)}
                              className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                              title="ویرایش"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                              title="حذف"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {/* Child Categories - Only show if expanded */}
                      {hasChildren && isExpanded && category.children?.map((child: AdminCategoryDto & { children?: AdminCategoryDto[] }) => {
                        const ChildIconComponent = getIconComponent(child.iconUrl || 'general');
                        const hasSubChildren = child.children && child.children.length > 0;
                        const isChildExpanded = expandedCategories.has(child.id);
                        
                        return (
                          <React.Fragment key={`child-fragment-${child.id}`}>
                            <tr 
                              key={`child-${child.id}`} 
                              className={`bg-gray-50 hover:bg-gray-100 ${draggedCategory === child.id ? 'opacity-50' : ''}`}
                              draggable
                              onDragStart={(e) => handleDragStart(e, child.id)}
                              onDragOver={handleDragOver}
                              onDrop={(e) => handleDrop(e, child.id)}
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center" style={{ paddingLeft: '30px' }}>
                                  <Bars3Icon 
                                    className="h-4 w-4 text-gray-400 ml-2 cursor-move" 
                                    title="کشیدن برای تغییر ترتیب"
                                  />
                                  <span className="text-sm font-medium text-gray-900">{child.sortOrder}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center" style={{ paddingLeft: '30px' }}>
                                  <ChildIconComponent className="h-6 w-6 text-gray-600" />
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center" style={{ paddingLeft: '30px' }}>
                                  {hasSubChildren && (
                                    <button
                                      onClick={() => toggleCategoryExpansion(child.id)}
                                      className="ml-2 w-6 h-6 flex items-center justify-center hover:bg-gray-200 rounded transition-colors"
                                    >
                                      {isChildExpanded ? (
                                        <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                                      ) : (
                                        <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
                                      )}
                                    </button>
                                  )}
                                  {!hasSubChildren && (
                                    <div className="ml-2 w-6 h-6 flex items-center justify-center">
                                      <span className="text-gray-400">└─</span>
                                    </div>
                                  )}
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 flex items-center">
                                      {child.name}
                                      {hasSubChildren && (
                                        <span className="ml-2 bg-primary-100 text-primary-800 text-xs px-2 py-0.5 rounded-full">
                                          {child.children?.length} زیردسته
                                        </span>
                                      )}
                                    </div>
                                    {child.description && (
                                      <div className="text-sm text-gray-500">
                                        {child.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-800">
                                  زیردسته
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">0</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => handleManageAttributes(child.id)}
                                  className="text-purple-600 hover:text-purple-900 text-sm font-medium flex items-center space-x-1 space-x-reverse"
                                >
                                  <TagIcon className="h-4 w-4" />
                                  <span>مدیریت ویژگی‌ها</span>
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2 space-x-reverse">
                                  <button
                                    onClick={() => handleEditCategory(child)}
                                    className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                                    title="ویرایش"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteCategory(child.id)}
                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                    title="حذف"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            
                            {/* Sub-subcategories (Level 3) - Only show if child is expanded */}
                            {hasSubChildren && isChildExpanded && child.children?.map((subChild: AdminCategoryDto) => {
                              const SubChildIconComponent = getIconComponent(subChild.iconUrl || 'general');
                              return (
                                <tr 
                                  key={`subchild-${subChild.id}`} 
                                  className={`bg-gray-100 hover:bg-gray-200 ${draggedCategory === subChild.id ? 'opacity-50' : ''}`}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, subChild.id)}
                                  onDragOver={handleDragOver}
                                  onDrop={(e) => handleDrop(e, subChild.id)}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center" style={{ paddingLeft: '60px' }}>
                                      <Bars3Icon 
                                        className="h-4 w-4 text-gray-400 ml-2 cursor-move" 
                                        title="کشیدن برای تغییر ترتیب"
                                      />
                                      <span className="text-sm font-medium text-gray-900">{subChild.sortOrder}</span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center" style={{ paddingLeft: '60px' }}>
                                      <SubChildIconComponent className="h-6 w-6 text-gray-600" />
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center" style={{ paddingLeft: '60px' }}>
                                      <div className="ml-2 w-6 h-6 flex items-center justify-center">
                                        <span className="text-gray-400">└─</span>
                                      </div>
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {subChild.name}
                                        </div>
                                        {subChild.description && (
                                          <div className="text-sm text-gray-500">
                                            {subChild.description}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
                                      زیر-زیردسته
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">0</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                      onClick={() => handleManageAttributes(subChild.id)}
                                      className="text-purple-600 hover:text-purple-900 text-sm font-medium flex items-center space-x-1 space-x-reverse"
                                    >
                                      <TagIcon className="h-4 w-4" />
                                      <span>مدیریت ویژگی‌ها</span>
                                    </button>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex items-center space-x-2 space-x-reverse">
                                      <button
                                        onClick={() => handleEditCategory(subChild)}
                                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                                        title="ویرایش"
                                      >
                                        <PencilIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteCategory(subChild.id)}
                                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                        title="حذف"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Category Attributes Management Modal */}
      {showAttributeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-11/12 max-w-6xl shadow-2xl rounded-xl bg-white">
            <div className="mt-3">
              {/* Enhanced Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <TagIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Category Attribute Management</h3>
                    {selectedCategoryForAttributes && (
                      <p className="text-sm text-gray-600 mt-1">
                        Category: <span className="font-medium text-purple-600">
                          {localCategories?.find((c: AdminCategoryDto) => c.id === selectedCategoryForAttributes)?.name}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowAttributeModal(false);
                    setSelectedCategoryForAttributes('');
                    setEditingAttribute(null);
                  }}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <span className="text-gray-500 text-lg">✕</span>
                </button>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
                {/* Enhanced Attribute Form */}
                <div className="xl:col-span-2 space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-primary-200">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                        <PlusIcon className="h-5 w-5 text-white" />
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        {editingAttribute ? 'ویرایش ویژگی' : 'افزودن ویژگی جدید'}
                      </h4>
                    </div>
                    
                    <div className="space-y-5">
                      {/* Name and Label Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">نام فیلد</label>
                          <input
                            type="text"
                            value={attributeForm.name}
                            onChange={(e) => setAttributeForm({ ...attributeForm, name: e.target.value })}
                            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="مثال: year_of_manufacture"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">برچسب نمایش</label>
                          <input
                            type="text"
                            value={attributeForm.label}
                            onChange={(e) => setAttributeForm({ ...attributeForm, label: e.target.value })}
                            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="مثال: سال تولید"
                          />
                        </div>
                      </div>

                      {/* Type Selection with Dropdown */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">نوع ویژگی</label>
                        <select
                          value={attributeForm.type}
                          onChange={(e) => setAttributeForm({ ...attributeForm, type: e.target.value })}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                        >
                          <option value="Text">📝 متن</option>
                          <option value="Number">🔢 عدد</option>
                          <option value="Boolean">✓✗ بله/خیر</option>
                          <option value="Date">📅 تاریخ</option>
                          <option value="Dropdown">📋 لیست کشویی</option>
                          <option value="MultiSelect">☑️ چند انتخابی</option>
                          <option value="TextArea">📄 متن طولانی</option>
                          <option value="Url">🔗 آدرس اینترنتی</option>
                          <option value="Email">📧 ایمیل</option>
                        </select>
                      </div>

                      {/* Options for Dropdown/MultiSelect */}
                      {(attributeForm.type === 'Dropdown' || attributeForm.type === 'MultiSelect') && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            گزینه‌ها
                            <span className="text-xs text-gray-500 font-normal ml-2">(یک گزینه در هر خط)</span>
                          </label>
                          <textarea
                            value={attributeForm.options}
                            onChange={(e) => setAttributeForm({ ...attributeForm, options: e.target.value })}
                            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            rows={4}
                            placeholder={`گزینه اول\nگزینه دوم\nگزینه سوم`}
                          />
                        </div>
                      )}

                      {/* Default Value and Sort Order */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">مقدار پیش‌فرض</label>
                          <input
                            type="text"
                            value={attributeForm.defaultValue}
                            onChange={(e) => setAttributeForm({ ...attributeForm, defaultValue: e.target.value })}
                            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="اختیاری"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">ترتیب نمایش</label>
                          <input
                            type="number"
                            value={attributeForm.sortOrder}
                            onChange={(e) => setAttributeForm({ ...attributeForm, sortOrder: Number(e.target.value) })}
                            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            min="0"
                          />
                        </div>
                      </div>

                      {/* Enhanced Checkboxes */}
                      <div className="space-y-3">
                        <h5 className="text-sm font-semibold text-gray-700">تنظیمات ویژگی</h5>
                        <div className="grid grid-cols-1 gap-3">
                          {[
                            { key: 'isRequired', label: 'فیلد الزامی', icon: '⚠️', desc: 'کاربر باید این فیلد را پر کند' },
                            { key: 'isFilterable', label: 'قابل فیلتر', icon: '🔍', desc: 'نمایش در فیلترهای صفحه جستجو' },
                            { key: 'isSearchable', label: 'قابل جستجو', icon: '🔎', desc: 'شامل در متن جستجو' }
                          ].map((setting) => (
                            <label key={setting.key} className="flex items-center p-3 bg-white rounded-lg border-2 border-gray-100 hover:border-gray-200 cursor-pointer transition-all">
                              <input
                                type="checkbox"
                                checked={attributeForm[setting.key as keyof typeof attributeForm] as boolean}
                                onChange={(e) => setAttributeForm({ ...attributeForm, [setting.key]: e.target.checked })}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                              />
                              <div className="ml-3">
                                <div className="flex items-center space-x-2 space-x-reverse">
                                  <span className="text-lg">{setting.icon}</span>
                                  <span className="text-sm font-medium text-gray-900">{setting.label}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{setting.desc}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-3 space-x-reverse pt-4">
                        <button
                          onClick={handleSaveAttribute}
                          className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-medium flex items-center justify-center space-x-2 space-x-reverse"
                        >
                          <span>{editingAttribute ? 'به‌روزرسانی' : 'افزودن'} ویژگی</span>
                          <PlusIcon className="h-4 w-4" />
                        </button>
                        {editingAttribute && (
                          <button
                            onClick={() => {
                              setEditingAttribute(null);
                              setAttributeForm({
                                name: '',
                                label: '',
                                type: 'Text',
                                isRequired: false,
                                isFilterable: false,
                                isSearchable: false,
                                sortOrder: 0,
                                options: '',
                                defaultValue: ''
                              });
                            }}
                            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium"
                          >
                            لغو ویرایش
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Existing Attributes List */}
                <div className="xl:col-span-3">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">{categoryAttributes?.data?.length || 0}</span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900">ویژگی‌های موجود</h4>
                      </div>
                    </div>
                    
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {!categoryAttributes?.data || categoryAttributes.data.length === 0 ? (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <TagIcon className="h-8 w-8 text-gray-400" />
                          </div>
                          <p className="text-gray-500 text-lg font-medium">هنوز ویژگی‌ای تعریف نشده است</p>
                          <p className="text-gray-400 text-sm mt-2">اولین ویژگی خود را با استفاده از فرم سمت راست اضافه کنید</p>
                          {/* Debug info */}
                          <p className="text-xs text-gray-400 mt-2">
                            Debug: {categoryAttributes?.data ? `${categoryAttributes.data.length} attributes` : 'No data loaded'}
                          </p>
                        </div>
                      ) : (
                        categoryAttributes.data.map((attr: CategoryAttribute) => (
                          <div key={attr.id} className="bg-white rounded-xl border-2 border-gray-100 hover:border-primary-200 transition-all p-5 group">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 space-x-reverse mb-3">
                                  <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white text-xs font-semibold">
                                      {attr.type === 'Text' ? '📝' : 
                                       attr.type === 'Number' ? '🔢' : 
                                       attr.type === 'Boolean' ? '✓' : 
                                       attr.type === 'Date' ? '📅' : 
                                       attr.type === 'Dropdown' ? '📋' : 
                                       attr.type === 'MultiSelect' ? '☑️' : 
                                       attr.type === 'TextArea' ? '📄' : 
                                       attr.type === 'Url' ? '🔗' : 
                                       attr.type === 'Email' ? '📧' : '📝'}
                                    </span>
                                  </div>
                                  <div>
                                    <h5 className="font-semibold text-gray-900 text-lg">{attr.label}</h5>
                                    <div className="flex items-center space-x-2 space-x-reverse mt-1">
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{attr.name}</span>
                                      <span className="text-xs bg-primary-100 text-primary-600 px-2 py-1 rounded-full">{attr.type}</span>
                                      {attr.isRequired && <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">الزامی</span>}
                                      {attr.isFilterable && <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">فیلتر</span>}
                                      {attr.isSearchable && <span className="text-xs bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full">جستجو</span>}
                                    </div>
                                  </div>
                                </div>
                                
                                {attr.options && (
                                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                    <p className="text-xs font-medium text-gray-600 mb-1">گزینه‌های موجود:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {attr.options.split('\n').filter(opt => opt.trim()).map((option, idx) => (
                                        <span key={idx} className="text-xs bg-white text-gray-700 px-2 py-1 rounded border">
                                          {option.trim()}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {attr.defaultValue && (
                                  <div className="mt-2">
                                    <span className="text-xs text-gray-500">پیش‌فرض: </span>
                                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">{attr.defaultValue}</span>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex space-x-2 space-x-reverse opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    setEditingAttribute(attr);
                                    setAttributeForm({
                                      name: attr.name,
                                      label: attr.label,
                                      type: attr.type,
                                      isRequired: attr.isRequired,
                                      isFilterable: attr.isFilterable,
                                      isSearchable: attr.isSearchable,
                                      sortOrder: attr.sortOrder,
                                      options: attr.options || '',
                                      defaultValue: attr.defaultValue || ''
                                    });
                                  }}
                                  className="w-9 h-9 bg-primary-100 hover:bg-primary-200 text-primary-600 rounded-lg flex items-center justify-center transition-colors"
                                  title="ویرایش"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    showModal(modalPresets.delete('این ویژگی', async () => {
                                      try {
                                        await adminApi.deleteCategoryAttribute(selectedCategoryForAttributes, attr.id);
                                        toast.success('ویژگی حذف شد');
                                        await refetchAttributes();
                                      } catch (error) {
                                        toast.error('خطا در حذف ویژگی');
                                      }
                                    }));
                                  }}
                                  className="w-9 h-9 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors"
                                  title="حذف"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" dir="rtl">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingCategory ? 'ویرایش دسته' : 'افزودن دسته جدید'}
                </h3>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Category Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    نام دسته *
                  </label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => {
                      setCategoryForm({ ...categoryForm, name: e.target.value });
                      // Auto-generate slug if slug field is empty and not editing
                      if (!editingCategory && !categoryForm.slug) {
                        const autoSlug = e.target.value
                          .toLowerCase()
                          .replace(/[خ]/g, 'kh')
                          .replace(/[ج]/g, 'j')
                          .replace(/[چ]/g, 'ch')
                          .replace(/[ش]/g, 'sh')
                          .replace(/[غ]/g, 'gh')
                          .replace(/[ف]/g, 'f')
                          .replace(/[ق]/g, 'q')
                          .replace(/[ث]/g, 's')
                          .replace(/[ص]/g, 's')
                          .replace(/[ض]/g, 'd')
                          .replace(/[ذ]/g, 'z')
                          .replace(/[ظ]/g, 'z')
                          .replace(/[ط]/g, 't')
                          .replace(/[ز]/g, 'z')
                          .replace(/[ر]/g, 'r')
                          .replace(/[د]/g, 'd')
                          .replace(/[س]/g, 's')
                          .replace(/[ع]/g, 'a')
                          .replace(/[م]/g, 'm')
                          .replace(/[ن]/g, 'n')
                          .replace(/[ت]/g, 't')
                          .replace(/[ل]/g, 'l')
                          .replace(/[ک]/g, 'k')
                          .replace(/[گ]/g, 'g')
                          .replace(/[ی]/g, 'y')
                          .replace(/[ه]/g, 'h')
                          .replace(/[و]/g, 'v')
                          .replace(/[پ]/g, 'p')
                          .replace(/[ب]/g, 'b')
                          .replace(/[ا]/g, 'a')
                          .replace(/[آ]/g, 'a')
                          .replace(/[ء]/g, '')
                          .replace(/[‌]/g, '-') // Persian ZWNJ
                          .replace(/\s+/g, '-')
                          .replace(/[^a-z0-9-]/g, '')
                          .replace(/-+/g, '-')
                          .replace(/^-|-$/g, '')
                          .trim();
                        setCategoryForm(prev => ({ ...prev, slug: autoSlug }));
                      }
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="مثال: الکترونیک، خودرو، خانه و باغ"
                  />
                </div>

                {/* Category Slug */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسلاگ *
                  </label>
                  <input
                    type="text"
                    value={categoryForm.slug}
                    onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-') })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="مثال: electronics, cars, home-garden"
                    pattern="[a-z0-9-]+"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    اسلاگ باید فقط شامل حروف کوچک انگلیسی، اعداد و خط تیره باشد. برای URL استفاده می‌شود.
                  </p>
                </div>

                {/* Category Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    توضیحات
                  </label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows={3}
                    placeholder="توضیحات مختصر درباره این دسته"
                  />
                </div>

                {/* Parent Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    دسته والد
                  </label>
                  <select
                    value={categoryForm.parentCategoryId}
                    onChange={(e) => setCategoryForm({ ...categoryForm, parentCategoryId: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">دسته اصلی (بدون والد)</option>
                    {localCategories.filter(cat => cat.id !== editingCategory?.id).map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.parentCategoryId ? '── ' : ''}{category.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    برای ایجاد زیردسته، یک دسته والد انتخاب کنید
                  </p>
                </div>

                {/* Icon Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    آیکون دسته
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowIconPicker(!showIconPicker)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-right flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <div className="flex items-center space-x-2 space-x-reverse">
                        {React.createElement(getIconComponent(categoryForm.iconUrl), { className: "h-5 w-5" })}
                        <span>
                          {CATEGORY_ICONS.find(icon => icon.value === categoryForm.iconUrl)?.name || 'انتخاب آیکون'}
                        </span>
                      </div>
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </button>
                    
                    {showIconPicker && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        <div className="grid grid-cols-3 gap-2 p-3">
                          {CATEGORY_ICONS.map((iconOption) => {
                            const IconComponent = iconOption.icon;
                            return (
                              <button
                                key={iconOption.value}
                                type="button"
                                onClick={() => {
                                  setCategoryForm({ ...categoryForm, iconUrl: iconOption.value });
                                  setShowIconPicker(false);
                                }}
                                className={`p-3 rounded-lg border-2 hover:bg-gray-50 flex flex-col items-center space-y-1 ${
                                  categoryForm.iconUrl === iconOption.value 
                                    ? 'border-primary-500 bg-primary-50' 
                                    : 'border-gray-200'
                                }`}
                              >
                                <IconComponent className="h-6 w-6 text-gray-600" />
                                <span className="text-xs text-gray-600 text-center">{iconOption.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ترتیب نمایش
                  </label>
                  <input
                    type="number"
                    value={categoryForm.sortOrder}
                    onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: Number(e.target.value) })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="1"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    پیشنهاد: {getNextSortOrder()} (عدد کمتر = نمایش زودتر)
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 space-x-reverse pt-4 border-t">
                  <button
                    onClick={() => setShowCategoryModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    لغو
                  </button>
                  <button
                    onClick={handleSaveCategory}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    {editingCategory ? 'به‌روزرسانی' : 'ایجاد'} دسته
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={confirm}
        onClose={cancel}
        title={modalConfig?.title || ''}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText || 'Confirm'}
        cancelText={modalConfig?.cancelText || 'Cancel'}
        type={modalConfig?.type || 'warning'}
      />
    </div>
  );
};

export default AdminCategoriesPage; 
